import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import {
  LIGHT_COLORS_RAW,
  DARK_COLORS_RAW,
  vals,
} from '@/constants/colors';
import { range } from '@/utils';
import useHasHydrated from '@/hooks/use-has-hydrated';
import { useColorMode } from '@/components/UserPreferencesProvider';

import Flags from './Flags';
import Peg from './Peg';
import Block from './Block';
import Arrow from './Arrow';
import TextFallback from './TextFallback';
import { formatFlags, getNumOfStackedFlags } from './Graph.helpers';
import type {
  GraphEvent,
  ArrowEventType,
  BlockEventType,
} from './ClientServerGraph.types';

const BORDER_WIDTH = 3;
const ROW_HEIGHT = 100;
const ROW_PADDING = 40;

export interface Props {
  firstPaint?: number;
  contentPaint?: number;
  pageInteractive?: number;
  events?: Array<GraphEvent>;
  columnWidth?: number;
  colorModeOverride?: 'light' | 'dark';
  marginTopOverride?: number;
  clientLabel?: string;
  serverLabel?: string;
  width?: number;
}

function Graph({
  firstPaint,
  contentPaint,
  pageInteractive,
  events = [],
  columnWidth = 16,
  colorModeOverride,
  marginTopOverride,
  clientLabel = 'Client',
  serverLabel = 'Server',
  width: innerWidth = 600,
}: Props) {
  const id = React.useId();

  const width = innerWidth + BORDER_WIDTH;

  const numOfColumns = Math.round(width / columnWidth);

  const formattedFlags = formatFlags(
    firstPaint,
    contentPaint,
    pageInteractive
  );
  const includeFlags = formattedFlags.length > 0;

  // The flags protrude up from the graph, so we'll add some space on top, so they don't overlap the rest of the content.
  const numOfStackedFlags = getNumOfStackedFlags(formattedFlags);

  const marginTop =
    typeof marginTopOverride === 'number'
      ? marginTopOverride
      : 64 + numOfStackedFlags * 32;

  // Ok, so React gets SUPER confused if we render all of the blocks and arrows together.
  const arrows = events
    .filter((event) => event.type === 'arrow')
    .sort((a, b) => {
      return a.key < b.key ? 1 : -1;
    }) as Array<ArrowEventType>;
  const blocks = events
    .filter((event) => event.type !== 'arrow')
    .sort((a, b) => {
      return a.key < b.key ? 1 : -1;
    }) as Array<BlockEventType>;

  return (
    <>
      <Wrapper
        layout
        layoutRoot
        data-can-be-annotated="false"
        style={{
          marginTop,
          '--width': width + 'px',
          '--num-of-columns': numOfColumns,
          '--column-width': columnWidth + 'px',
        }}
      >
        {range(0, numOfColumns, 10).map((distance) => (
          <Peg
            key={distance}
            distance={distance}
            columnWidth={columnWidth}
          />
        ))}
        <RowLabel
          style={{
            top: ROW_PADDING + ROW_HEIGHT,
          }}
        >
          {serverLabel}
        </RowLabel>
        <RowLabel
          style={{
            top: ROW_PADDING * 2 + ROW_HEIGHT * 2,
          }}
        >
          {clientLabel}
        </RowLabel>
        <RowBackground style={{ gridRow: 2 }} />
        <RowBackground style={{ gridRow: 4 }} />

        <>
          {includeFlags && (
            <Flags
              formattedFlags={formattedFlags}
              columnWidth={columnWidth}
            />
          )}
        </>

        {arrows.map(({ key, ...eventData }, index) => {
          const eventId = `${id}-${key ?? index}`;

          return (
            <Arrow
              key={eventId}
              columnWidth={columnWidth}
              {...eventData}
            />
          );
        })}
        {blocks.map(({ key, ...eventData }, index) => {
          const eventId = `${id}-${key ?? index}`;

          return (
            <Block
              colorModeOverride={colorModeOverride}
              key={eventId}
              {...eventData}
            />
          );
        })}

        {/* Add a gradient along the right edge to fade out any content that overflows, to show that they can scroll. */}
        <GridEdge data-color-mode-override={colorModeOverride} />
      </Wrapper>

      <TextFallback events={events} />
    </>
  );
}

const Wrapper = styled(motion.div)`
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-rows:
    ${ROW_PADDING}px ${ROW_HEIGHT}px
    ${ROW_PADDING}px ${ROW_HEIGHT}px
    ${ROW_PADDING}px;
  grid-template-columns: repeat(
    var(--num-of-columns),
    var(--column-width)
  );
  margin-bottom: 50px;
  border-left: ${BORDER_WIDTH}px solid var(--color-text);
  border-bottom: ${BORDER_WIDTH}px solid var(--color-text);
  border-radius: ${BORDER_WIDTH / 2}px 0px ${BORDER_WIDTH / 2}px 0px;
  width: calc(var(--width) - 1px);
  background: repeating-linear-gradient(
      to right,
      transparent 0px,
      transparent calc(var(--column-width) - 1px),
      var(--color-gray-200) calc(var(--column-width) - 1px),
      var(--color-gray-200) var(--column-width)
    ),
    repeating-linear-gradient(
      to right,
      transparent 0px,
      transparent calc(var(--column-width) * 10 - 2px),
      var(--color-gray-200) calc(var(--column-width) * 10 - 2px),
      var(--color-gray-200) calc(var(--column-width) * 10)
    );
  background-repeat: repeat;
`;

const RowLabel = styled.div`
  position: absolute;
  left: 0;
  width: ${ROW_HEIGHT}px;
  transform: translateY(-100%) rotate(-90deg) translateY(-6px);
  transform-origin: bottom left;
  font-size: 1.25rem;
  text-align: center;
  white-space: nowrap;
`;

const RowBackground = styled.div`
  grid-column: 1 / -1;
  background: hsl(217deg 20% 50% / 0.07);
  border-top: 1px solid var(--color-gray-300);
  border-bottom: 1px solid var(--color-gray-300);
`;

const GridEdge = styled.div`
  grid-row: 1 / -1;
  grid-column: -3 / -1;
  background: linear-gradient(
    to right,
    hsl(var(--fade-hsl) / 0),
    hsl(var(--fade-hsl) / 1)
  );

  html[data-color-mode='dark'] & {
    --fade-hsl: ${vals(DARK_COLORS_RAW.colorBackground)};
  }
  html[data-color-mode='light'] & {
    --fade-hsl: ${vals(LIGHT_COLORS_RAW.colorBackground)};
  }

  html[data-color-mode='dark'] &[data-color-mode-override='light'] {
    --fade-hsl: ${vals(DARK_COLORS_RAW.colorAdaptiveWhite)};
  }
`;

export default React.memo(Graph);
